import { useCallback } from 'react';

import type { ReviewQuestion } from '@/types/review/ReviewQuestion';
import type { ReviewTemplate } from '@/types/review/ReviewTemplate';
import type { LocalizedText } from '@/utils/localization';

import { ReviewQuestionStatus } from '@/constants/questionnaire/Question';
import {
    ManagerAssessmentSkillSelectorOption,
    type ManagerAssessmentSkillSelectorOptionValue,
} from '@/constants/review/ManagerAssessmentSkillSelectorOption';
import { useAmelioTranslations } from '@/hooks';
import { useError } from '@/hooks/utils/useError';
import { showWarningToast } from '@/plugins/Toast';

const MANAGER_SKILL_SELECTOR_OPTIONS: ManagerAssessmentSkillSelectorOptionValue[] = Object.values(
    ManagerAssessmentSkillSelectorOption,
);

export const normalizeManagerSkillSelectorOption = (
    value: unknown,
): ManagerAssessmentSkillSelectorOptionValue | null => {
    if (
        typeof value === 'string' &&
        MANAGER_SKILL_SELECTOR_OPTIONS.includes(value as ManagerAssessmentSkillSelectorOptionValue)
    ) {
        return value as ManagerAssessmentSkillSelectorOptionValue;
    }

    if (typeof value === 'number' && value >= 0 && value < MANAGER_SKILL_SELECTOR_OPTIONS.length) {
        return MANAGER_SKILL_SELECTOR_OPTIONS[value] ?? null;
    }

    return null;
};

interface UseSkillSelectorPersistenceParams {
    template: ReviewTemplate | undefined;
    title: LocalizedText | undefined;
    questions: ReviewQuestion[];
    selectedOption: ManagerAssessmentSkillSelectorOptionValue | null;
    updateTemplate: (template: ReviewTemplate) => Promise<unknown>;
    onOptionChange: (option: ManagerAssessmentSkillSelectorOptionValue) => void;
}

export const useSkillSelectorPersistence = ({
    template,
    title,
    questions,
    selectedOption,
    updateTemplate,
    onOptionChange,
}: UseSkillSelectorPersistenceParams) => {
    const { t } = useAmelioTranslations();
    const { handleError } = useError();

    const persistedOption = normalizeManagerSkillSelectorOption(template?.skillSelectorOption);

    const persistOption = useCallback(
        async (option: ManagerAssessmentSkillSelectorOptionValue): Promise<boolean> => {
            if (!template) return false;

            try {
                await updateTemplate({
                    ...template,
                    title: title ?? {},
                    description: template.description ?? '',
                    skillSelectorOption: option,
                    questions,
                });
                return true;
            } catch (error) {
                handleError({ error });
                return false;
            }
        },
        [template, title, questions, updateTemplate, handleError],
    );

    const onSkillSelectorChange = useCallback(
        async (newOption: ManagerAssessmentSkillSelectorOptionValue) => {
            if (newOption === selectedOption) return;

            if (
                newOption === ManagerAssessmentSkillSelectorOption.DO_NOT_ASSESS_SKILLS &&
                !questions.some((q) => q.status === ReviewQuestionStatus.ACTIVE)
            ) {
                showWarningToast({
                    message: t(
                        'templateManagement.template.error.cannotSelectDoNotAssessSkillsWithoutActiveQuestion',
                    ),
                });
                return;
            }

            onOptionChange(newOption);
            await persistOption(newOption);
        },
        [selectedOption, questions, onOptionChange, persistOption, t],
    );

    // Question writes persist immediately while the selector only persists via template
    // writes — flush the pending option first so the server never stores
    // DoNotAssessSkills with zero active questions.
    const ensureOptionPersisted = useCallback(async (): Promise<boolean> => {
        if (persistedOption !== ManagerAssessmentSkillSelectorOption.DO_NOT_ASSESS_SKILLS) {
            return true;
        }
        if (
            !selectedOption ||
            selectedOption === ManagerAssessmentSkillSelectorOption.DO_NOT_ASSESS_SKILLS
        ) {
            return false;
        }
        return persistOption(selectedOption);
    }, [persistedOption, selectedOption, persistOption]);

    return { onSkillSelectorChange, ensureOptionPersisted };
};
